import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import logo from '../assets/logo.png';

const pillars = [
  {
    glyph: '♟',
    num: '01',
    title: 'The Programme',
    text: 'Group workshops, one-to-one coaching, and a social ship’s tournament — a complete enrichment residency with zero overhead to the line.',
    to: '/programme',
  },
  {
    glyph: '♜',
    num: '02',
    title: 'Technology',
    text: 'The SenseRobot AI chess robot, premium electronic boards, and a full tournament kit — owned, transported, and managed by the instructor.',
    to: '/technology',
  },
  {
    glyph: '♛',
    num: '03',
    title: 'Wellbeing',
    text: 'Chess as a community intervention — for NHS social prescribing, schools enrichment, and community health, backed by a deep evidence base.',
    to: '/wellbeing',
  },
  {
    glyph: '♞',
    num: '04',
    title: 'Events',
    text: 'From grassroots schools tournaments to AI robot exhibition matches with grandmaster Michael Adams — chess brought vividly to life.',
    to: '/events',
  },
];

const stats = [
  { num: '25', label: 'AI difficulty levels on the SenseRobot' },
  { num: '£5m', label: 'Public liability insurance' },
  { num: 'CSC', label: 'Qualified chess coach' },
  { num: '1,200+', label: 'Structured chess exercises onboard' },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="home-hero">
        <img src={logo} alt="Chess at Sea emblem" className="home-emblem fade-up d1" />
        <p className="eyebrow fade-up d2">Enrichment for the world&rsquo;s great ocean liners</p>
        <h1 className="fade-up d3">Chess that passengers <em>remember</em></h1>
        <p className="lede fade-up d4">
          A professional, fully-equipped chess enrichment residency — group workshops, private
          coaching, an onboard tournament, and AI chess technology most passengers have never seen.
        </p>
        <div className="hero-actions fade-up d5">
          <Link to="/programme" className="btn-primary">
            Explore the Programme <span className="arrow">→</span>
          </Link>
          <Link to="/about#contact" className="btn-ghost">Get in Touch</Link>
        </div>
        <div className="scroll-cue">
          <span>Scroll</span>
          <span className="line" />
        </div>
      </section>

      {/* INTRO */}
      <section className="home-intro">
        <div className="inner">
          <div className="intro-grid">
            <Reveal>
              <p className="section-tag">A skill they take home</p>
              <h2>Enrichment that <em>lasts longer</em> than the voyage</h2>
            </Reveal>
            <Reveal className="intro-body" delay={120}>
              <p>
                Chess at Sea delivers something almost no other enrichment activity can: a genuine
                skill that passengers keep long after they disembark. A complete beginner can leave
                the voyage knowing how to play — a gift they will share with family and friends for
                the rest of their lives.
              </p>
              <p>
                Founded and run by Robert Palser — a CSC-qualified chess coach with full safeguarding
                certification and £5&nbsp;million public liability insurance — the programme is
                credentialed, compliant, and designed to slot seamlessly into your onboard schedule.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="pillars on-dark">
        <div className="inner">
          <Reveal>
            <p className="section-tag">What we bring aboard</p>
            <h2 className="section-title">Four ways chess <em>comes to life</em></h2>
            <div className="divider" />
          </Reveal>
          <div className="pillars-grid">
            {pillars.map((p, i) => (
              <Reveal key={p.to} delay={i * 90}>
                <Link to={p.to} className="pillar">
                  <span className="pillar-num">{p.num}</span>
                  <span className="pillar-glyph">{p.glyph}</span>
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                  <span className="pillar-link">Discover more <span className="arrow">→</span></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SENSEROBOT FEATURE */}
      <section className="home-feature">
        <div className="inner">
          <div className="feature-grid">
            <Reveal className="feature-visual">
              <span className="robo">♜</span>
              <div className="badge">CES 2025 · World First</div>
            </Reveal>
            <Reveal className="feature-text" delay={120}>
              <p className="section-tag">The centrepiece</p>
              <h2 className="section-title">A robot that <em>physically</em> plays chess</h2>
              <div className="divider" />
              <p className="section-body">
                The SenseRobot is the world&rsquo;s first AI chess robot with a physical robotic arm
                that moves real pieces on a real board — not a screen, not a simulation. Most
                passengers will never have seen anything like it.
              </p>
              <ul className="mini-list">
                <li><span className="k">Millimetre-precision arm</span> — moves real pieces in front of you</li>
                <li><span className="k">25 difficulty levels</span> — from absolute beginner to near-world-champion</li>
                <li><span className="k">Voice coaching</span> — a tireless tutor, always available</li>
              </ul>
              <div style={{ marginTop: 34 }}>
                <Link to="/technology" className="btn-primary">
                  See the Technology <span className="arrow">→</span>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CREDENTIALS BAND */}
      <section className="creds-band on-dark">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Credentialed &amp; compliant</p>
            <h2 className="section-title">Trusted to deliver, <em>anywhere</em></h2>
            <div className="divider" />
          </Reveal>
          <Reveal delay={120}>
            <div className="creds-row">
              {stats.map((s) => (
                <div className="cred-stat" key={s.label}>
                  <div className="num">{s.num}</div>
                  <div className="label">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* EVENT TEASER */}
      <section className="event-teaser">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Headline event 2026</p>
            <h2 className="section-title">Chess at Sea <em>in action</em></h2>
            <div className="divider" />
          </Reveal>
          <Reveal delay={120}>
            <div className="teaser-card">
              <div className="teaser-date">
                <div className="d">3</div>
                <div className="m">July</div>
                <div className="y">2026</div>
              </div>
              <div className="teaser-body">
                <p className="ttag">Headline Sponsor · Special guest GM Michael Adams</p>
                <h3>The Great Merthyr Schools Chess Tournament</h3>
                <p>
                  Five rounds of Swiss-system chess for 100+ junior players — featuring a
                  grandmaster simultaneous exhibition and a live SenseRobot vs Michael Adams match.
                </p>
              </div>
              <Link to="/events" className="btn-primary">
                View the Day <span className="arrow">→</span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band deep">
        <Reveal>
          <h2>Bring Chess <em>at Sea</em> aboard</h2>
          <p>
            I would welcome a conversation about how this programme can work for your fleet,
            your passengers, or your community.
          </p>
          <Link to="/about#contact" className="btn-primary">
            Get in Touch <span className="arrow">→</span>
          </Link>
        </Reveal>
      </section>
    </>
  );
}
